import './galleryStrip.css';
import {Component} from "react";
import * as React from "react";
import PageSection from "../structural/pageSection";
import {ArtEntryDto, CategoryDto} from "../../service/model/dto";
import GalleryApiService from "../../service/galleryApiService";
import Button from "../structural/button";
import {ErrorDetails, RestCall} from "../../service/restService";

type DisplayItem = ArtEntryDto | ArtEntryDto[];

/** Primary pair plus optional gallery pairs as separate modal carousel rows for one piece. */
function expandPieceToCarouselSlots(piece: ArtEntryDto): ArtEntryDto[] {
    const base: ArtEntryDto = {
        ...piece,
        images: {
            full: piece.images.full,
            preview: piece.images.preview
        }
    };
    const extras = piece.images.gallery;
    if (!extras?.length) return [base];
    const rest = extras.map((pair) => ({
        ...piece,
        images: {full: pair.full, preview: pair.preview}
    }));
    return [base, ...rest];
}

interface CategorySection {
    categoryName: string;
    items: DisplayItem[];
}

interface GalleryStripState {
    art: ArtEntryDto[];
    categories: CategoryDto[];
    sections: CategorySection[];
    shownModal: number;
    /** Index within current set when display item is an array; 0 for single piece */
    modalImageIndex: number;
}

class GalleryStrip extends Component<{}, GalleryStripState> {

    constructor(props: {}) {
        super(props, {})
        this.state = {
            art: [],
            categories: [],
            sections: [],
            shownModal: -1,
            modalImageIndex: 0
        };

        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleCloseModal = this.handleCloseModal.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
    }



    async componentDidMount() {
        let art: ArtEntryDto[] = [];
        let categories: CategoryDto[] = [];
        const done = () => {
            const sections = this.buildSectionsFrom(art, categories);
            this.setState({art, categories, sections});
        };
        let artDone = false;
        let catDone = false;
        const maybeDone = () => {
            if (artDone && catDone) done();
        };
        GalleryApiService.getGalleryData(
            async (res) => { art = res.body; artDone = true; maybeDone(); },
            async (r: RestCall, e: ErrorDetails) => { console.error(e.error); artDone = true; maybeDone(); }
        );
        GalleryApiService.getCategories(
            async (res) => { categories = res.body; catDone = true; maybeDone(); },
            async () => { catDone = true; maybeDone(); }
        );
        document.addEventListener('keydown', this.handleKeyDown);
    }

    /** One row per image angle: each piece contributes primary + its gallery slots; sets are flattened across pieces. */
    carouselRow(item: DisplayItem): ArtEntryDto[] {
        if (Array.isArray(item)) return item.flatMap(expandPieceToCarouselSlots);
        return expandPieceToCarouselSlots(item);
    }

    buildSectionsFrom(art: ArtEntryDto[], categories: CategoryDto[]): CategorySection[] {
        if (!categories.length) return [];
        const order = categories.slice().sort((a, b) => a.displayOrder - b.displayOrder);
        const byCat = new Map<string, ArtEntryDto[]>();
        for (const a of art) {
            const cid = a.categoryId ?? "";
            if (!byCat.has(cid)) byCat.set(cid, []);
            byCat.get(cid)!.push(a);
        }
        const sections: CategorySection[] = [];
        for (const cat of order) {
            const list = byCat.get(cat.id) ?? [];
            const bySet = new Map<string, ArtEntryDto[]>();
            for (let i = 0; i < list.length; i++) {
                const a = list[i];
                const sid = a.setId && a.setId.trim() !== "" ? a.setId : null;
                const key = sid ?? "_single_" + i;
                if (!bySet.has(key)) bySet.set(key, []);
                bySet.get(key)!.push(a);
            }
            const items: DisplayItem[] = Array.from(bySet.values()).map((group) => {
                const sorted = [...group].sort((a, b) => {
                    const oa = a.displayOrder ?? 0;
                    const ob = b.displayOrder ?? 0;
                    if (oa !== ob) return oa - ob;
                    return (a.id ?? "").localeCompare(b.id ?? "");
                });
                return sorted.length === 1 ? sorted[0] : sorted;
            });
            sections.push({categoryName: cat.name, items});
        }
        return sections;
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown)
    }

    handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            this.handleCloseModal();
        } else if (event.key === "ArrowLeft" && this.state.shownModal > -1) {
            this.handlePrevious();
        } else if (event.key === "ArrowRight" && this.state.shownModal > -1) {
            this.handleNext();
        }
    }

    getFlatItems(): DisplayItem[] {
        return this.state.sections.flatMap((s) => s.items);
    }

    handlePrevious = () => {
        const flat = this.getFlatItems();
        if (flat.length === 0) return;
        const item = flat[this.state.shownModal];
        const row = this.carouselRow(item);
        const rowLen = row.length;
        if (rowLen > 1 && this.state.modalImageIndex > 0) {
            this.setState({modalImageIndex: this.state.modalImageIndex - 1});
        } else {
            const prevSlot = this.state.shownModal === 0 ? flat.length - 1 : this.state.shownModal - 1;
            const prevItem = flat[prevSlot];
            const prevLen = this.carouselRow(prevItem).length;
            this.setState({shownModal: prevSlot, modalImageIndex: prevLen - 1});
        }
    }

    handleNext = () => {
        const flat = this.getFlatItems();
        if (flat.length === 0) return;
        const item = flat[this.state.shownModal];
        const row = this.carouselRow(item);
        const rowLen = row.length;
        if (rowLen > 1 && this.state.modalImageIndex < rowLen - 1) {
            this.setState({modalImageIndex: this.state.modalImageIndex + 1});
        } else {
            const nextSlot = this.state.shownModal === flat.length - 1 ? 0 : this.state.shownModal + 1;
            this.setState({shownModal: nextSlot, modalImageIndex: 0});
        }
    }

    handleOverlayClick(i: number) {
        this.setState({shownModal: i, modalImageIndex: 0});
    }

    handleCloseModal(){
        if (this.state.shownModal !== -1) {
            this.setState({
                shownModal: -1
            })
        }
    };


    getOverlayButtonText(art : ArtEntryDto) {
        if (art.sold) {
            return "Sold";
        } else if (art.shopLink) {
            return "For sale!";
        } else {
            return "Available Soon";
        }
    }

    getOverlayButtonStyle(art : ArtEntryDto) {
        if (art.sold) {
            return "overlay-button-sold";
        } else if (art.shopLink) {
            return "overlay-button-available";
        } else {
            return "overlay-button-soon";
        }
    }

    openShopLink(event: any, art: ArtEntryDto) {
        event.stopPropagation();
        window.open(art.shopLink, "_blank");
    }


    getFirstArt(item: DisplayItem): ArtEntryDto {
        return Array.isArray(item) ? item[0] : item;
    }

    render() {
        const flatItems = this.getFlatItems();
        let globalIndex = 0;
        return (
            <PageSection id={"GalleryStrip"}>
                <div className={"Gallery-Strip"}>
                    <div className={"Gallery-Title"}>Gallery</div>
                    {this.state.sections.map((section, sectionIdx) => (
                        <div key={"section-" + sectionIdx} className={"Gallery-Section"}>
                            <h2 className={"Gallery-Section-Title"}>{section.categoryName}</h2>
                            <div className={"Gallery-Grid"}>
                                {section.items.map((item) => {
                                    const idx = globalIndex++;
                                    const art = this.getFirstArt(item);
                                    const carousel = this.carouselRow(item);
                                    const multiVisual = carousel.length > 1;
                                    return (
                                        <div className={"Gallery-Grid-Entry"} key={"slot-" + idx}>
                                            <div className={"Gallery-Image-Wrapper"}>
                                                <img
                                                    className={"Gallery-Image"}
                                                    src={art.images.preview}
                                                    alt={art.name}
                                                    loading={idx < 4 ? "eager" : "lazy"}
                                                />
                                                {multiVisual && (
                                                    <span className="Gallery-Grid-Entry-SetBadge" title={`${carousel.length} images`} aria-hidden="true">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="3" y="3" width="14" height="14" rx="1" />
                                                            <rect x="7" y="7" width="14" height="14" rx="1" />
                                                        </svg>
                                                        <span className="Gallery-Grid-Entry-SetCount">{carousel.length}</span>
                                                    </span>
                                                )}
                                                <div className="overlay" onClick={() => this.handleOverlayClick(idx)}>
                                                    <div className="overlay-content">
                                                        <div>{art.name}</div>
                                                        <div
                                                            onClick={(e) => art.shopLink && this.openShopLink(e, art)}
                                                            className={this.getOverlayButtonStyle(art)}
                                                        >
                                                            {this.getOverlayButtonText(art)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {this.state.shownModal > -1 && flatItems.length > 0 && (() => {
                        const item = flatItems[this.state.shownModal];
                        const row = this.carouselRow(item);
                        const art = row[this.state.modalImageIndex] ?? row[0];
                        return (
                            <div className="modal">
                                <div className="modal-content">
                                    <span className="close" onClick={this.handleCloseModal}>&times;</span>
                                    <button className="modal-nav modal-nav-prev" onClick={this.handlePrevious} aria-label="Previous">&#8249;</button>
                                    <button className="modal-nav modal-nav-next" onClick={this.handleNext} aria-label="Next">&#8250;</button>
                                    <div className={"Modal-Data"}>
                                        <div className="Gallery-Modal-MainColumn">
                                            <div className={"Gallery-Image-Wrapper-Full"}>
                                                <img
                                                    className={"Gallery-Image-Full"}
                                                    src={art.images.full}
                                                    alt={art.name}
                                                />
                                            </div>
                                            {row.length > 1 && (
                                                <div className="Gallery-Modal-ThumbStrip">
                                                    {row.map((a, idx) => (
                                                        <button
                                                            key={`${String(a.id ?? "")}-${idx}`}
                                                            type="button"
                                                            className={"Gallery-Modal-Thumb" + (idx === this.state.modalImageIndex ? " Gallery-Modal-Thumb--active" : "")}
                                                            onClick={() => this.setState({modalImageIndex: idx})}
                                                            aria-label={"Image " + (idx + 1)}
                                                        >
                                                            <img
                                                                src={a.images.preview}
                                                                alt=""
                                                                loading="lazy"
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className={"Gallery-Modal-Data-Wrapper"}>
                                            <div className={"Gallery-Modal-Data-Title"}>{art.name}</div>
                                            {art.description && <div className={"Gallery-Modal-Data-Description"}>{art.description}</div>}
                                            {(art.dimensions?.width?.trim() || art.dimensions?.height?.trim() || art.dimensions?.depth?.trim()) && (
                                            <div className={"Gallery-Modal-Data-Dimensions"}>
                                                {art.dimensions?.width?.trim() && <div><span className={"dimension-label"}>Width</span><span className={"dimension-value"}>{art.dimensions.width}</span></div>}
                                                {art.dimensions?.height?.trim() && <div><span className={"dimension-label"}>Height</span><span className={"dimension-value"}>{art.dimensions.height}</span></div>}
                                                {art.dimensions?.depth?.trim() && <div><span className={"dimension-label"}>Depth</span><span className={"dimension-value"}>{art.dimensions.depth}</span></div>}
                                            </div>
                                            )}
                                            <div className={"Gallery-Modal-Data-Button-Wrapper"}>
                                                <Button
                                                    value={art.sold ? "Sold" : art.shopLink ? "Buy Now" : "Available Soon"}
                                                    onClick={() => { if (!art.sold && art.shopLink) window.open(art.shopLink, "_blank"); }}
                                                    enabled={!art.sold && !!art.shopLink}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={"Modal-Mobile-Button-Bar"}>
                                        <button className="modal-nav-mobile modal-nav-prev-mobile" onClick={this.handlePrevious} aria-label="Previous">&#8249;</button>
                                        <Button value={art.sold ? "Sold" : art.shopLink ? "Buy Now" : "Available Soon"} onClick={() => { if (!art.sold && art.shopLink) window.open(art.shopLink, "_blank"); }} enabled={!art.sold && !!art.shopLink} />
                                        <button className="modal-nav-mobile modal-nav-next-mobile" onClick={this.handleNext} aria-label="Next">&#8250;</button>
                                    </div>
                                </div>
                                <style>{'body { overflow: hidden; }'}</style>
                            </div>
                        );
                    })()}
                </div>
            </PageSection>
        );
    }
}

export default GalleryStrip;